/**
 * Music beds (DESIGN-client §8, `docs/AUDIO-MUSIC.md`).
 *
 * v1 shipped with a music bus, a music volume slider and no music. The three tracks under
 * `packages/client/tools/vendor/music/` are the project owner's own; `tools/audio/pack-music.ts`
 * re-encodes them to `public/assets/music/<id>.{webm,mp3}`. Nothing here is generated, and nothing
 * here may be replaced by anything generated.
 *
 * Streamed, not decoded. An `HTMLAudioElement` feeds `createMediaElementSource` into the existing
 * music bus, so the browser holds a few seconds of the file rather than the whole thing: the lobby
 * bed is 205 s, which as a decoded `AudioBuffer` would be about 78 MB of resident memory on a
 * phone. The bus already sits behind the announcer/voice duck node, so speech ducks the music for
 * free and this module never touches `applyDuck`.
 *
 * Two halves, deliberately split:
 *
 *   - `MusicMachine` is pure. Given a snapshot of the store it answers "which bed, if any", and
 *     that is the whole of the screen/hand/format mapping. It owns one piece of memory, the
 *     match-end dwell, because "keep playing what was playing for six seconds" cannot be derived
 *     from a snapshot alone.
 *   - `MusicDirector` owns the sources, the crossfade and the timers, and asks the machine what to
 *     do on every store change. It never inspects the store itself: the snapshot arrives through a
 *     port, the same seam `installAudio` uses, so `audio/**` stays importable from a unit test.
 */
import { rampParam, type AudioEngine } from './AudioEngine';

export type MusicTrack = 'lobby' | 'game' | 'final_round';

/** Ids are also the file names; `tools/audio/pack-music.ts` writes exactly these. */
export const MUSIC_TRACKS: readonly MusicTrack[] = ['lobby', 'game', 'final_round'];

/** Long enough to read as a change of scene, short enough not to sound like a mistake. */
export const CROSSFADE_MS = 1200;

/**
 * How long the winning hand's bed survives the end of the match.
 *
 * The end screen lands on a held note and then a count-up; cutting to the lobby loop on the frame
 * the last trick resolves throws the result away. This is a little longer than
 * `HAND_END_MIN_DWELL_MS` on purpose — the match end is the one screen anybody lingers on.
 */
export const MATCH_END_DWELL_MS = 6000;

/** Classic: within this of the target and the next hand can end it. Mirrors the engine's `near`. */
export const MATCH_POINT_MARGIN = 100;

/** The `format` half of the table's rules, as much of it as the bed choice needs. */
export type MusicFormat = { kind: 'quick'; hands: number } | { kind: 'classic'; target: number } | { kind: 'money' };

/** Everything the machine reads. The installer fills it from one store snapshot. */
export interface MusicSnapshot {
  /** `presentation.screen`. */
  screen: string;
  /** `table.phase`, or null with no table. */
  phase: string | null;
  /** `table.handNo`; 1-based, 0 before the first deal. */
  handNo: number;
  /** `table.suddenDeath`; > 0 means the scheduled hands are used up and this one is extra. */
  suddenDeath: number;
  scores: readonly [number, number];
  format: MusicFormat | null;
  /** `presentation.layoutMode`; `pip` is a thumbnail beside a Discord call and gets no bed. */
  layoutMode: string;
  /** `document.hidden`. */
  hidden: boolean;
  /** `settings.volume.music`, 0..1. Exactly 0 means "off", not "quiet". */
  volume: number;
}

/** What the director should be doing right now. */
export interface MusicPlan {
  /** The bed that should be audible, or null for "no music at all, release everything". */
  track: MusicTrack | null;
  /** Keep the bed loaded and its position, but silent: a hidden tab or a PIP thumbnail. */
  suspended: boolean;
}

/** Screens that exist before a match does. Boot and the tutorial are lobby music by definition. */
const PRE_MATCH_SCREENS = new Set(['boot', 'tutorial', 'lobby']);

/** Phases in which no hand is being played. */
const IDLE_PHASES = new Set(['', 'lobby', 'matchEnd']);

/**
 * Is a hand actually in progress?
 *
 * Judged on the phase rather than the screen, because the screen lags it: `handEnd` sits over a
 * live table between hands and must keep the table's bed rather than dropping to the lobby loop
 * for six seconds, twelve times a match.
 */
export function isTableLive(s: MusicSnapshot): boolean {
  if (PRE_MATCH_SCREENS.has(s.screen)) return false;
  return s.phase != null && !IDLE_PHASES.has(s.phase);
}

/**
 * Is this the hand that can end the match?
 *
 * Deliberately derived from the table rather than from the `matchPoint` cue. That cue only fires
 * when one team leads (`packages/engine/src/reducer.ts`), so a dead-level score going into the
 * last hand — the most final-feeling hand there is — never emits one.
 */
export function isFinalHand(s: MusicSnapshot): boolean {
  if (!s.format) return false;
  // Every scheduled hand has been played and the match is still tied: whatever is being played now
  // is by definition the decider.
  if (s.suddenDeath > 0) return true;
  switch (s.format.kind) {
    case 'money':
      return true;
    case 'quick':
      return s.handNo >= s.format.hands;
    case 'classic':
      return Math.max(s.scores[0], s.scores[1]) >= s.format.target - MATCH_POINT_MARGIN;
  }
}

/** The bed for a snapshot, ignoring the match-end dwell and every reason to be silent. */
export function selectTrack(s: MusicSnapshot): MusicTrack {
  if (!isTableLive(s)) return 'lobby';
  return isFinalHand(s) ? 'final_round' : 'game';
}

/**
 * The screen/hand/format mapping plus the one bit of memory it needs.
 *
 * Deterministic in `(snapshot, now)`: hand it a fake clock and it is fully testable without a
 * browser, an `AudioContext` or a store.
 */
export class MusicMachine {
  /** The last bed a live table was playing; what the match-end dwell holds on to. */
  private lastLive: MusicTrack = 'lobby';
  /**
   * When the match-end dwell started, or null when no dwell is running.
   *
   * Null rather than 0: with a fake clock (and on the first tick of a real one) `now` is legitimately
   * 0, so a numeric sentinel would read as "no dwell" on the one frame that starts it and the bed
   * would cut to the lobby immediately.
   */
  private dwellFrom: number | null = null;

  plan(s: MusicSnapshot, now: number): MusicPlan {
    // Zero is off, not quiet: a slider at zero must not cost a single byte of network, so the
    // director tears its sources down rather than holding a silent stream open.
    if (!(s.volume > 0)) {
      this.dwellFrom = null;
      return { track: null, suspended: false };
    }
    return { track: this.select(s, now), suspended: s.hidden || s.layoutMode === 'pip' };
  }

  private select(s: MusicSnapshot, now: number): MusicTrack {
    if (isTableLive(s)) {
      this.dwellFrom = null;
      this.lastLive = isFinalHand(s) ? 'final_round' : 'game';
      return this.lastLive;
    }
    const ended = s.screen === 'matchEnd' || s.phase === 'matchEnd';
    if (!ended) {
      this.dwellFrom = null;
      return 'lobby';
    }
    this.dwellFrom ??= now;
    return now - this.dwellFrom < MATCH_END_DWELL_MS ? this.lastLive : 'lobby';
  }

  /**
   * ms until the plan could change without any further input, or null when it is stable.
   *
   * Only the match-end dwell has this property: nothing else in the mapping is a function of time,
   * so the director arms a timer only while a dwell is running.
   */
  nextDeadlineMs(now: number): number | null {
    if (this.dwellFrom === null) return null;
    const left = MATCH_END_DWELL_MS - (now - this.dwellFrom);
    return left > 0 ? left : null;
  }
}

// ---------------------------------------------------------------------------------------------
// Sources

/** One loaded bed. The default implementation is an `HTMLAudioElement` on the music bus. */
export interface MusicSource {
  /** Start (or resume) playback. Safe to call when already playing. */
  play(): void;
  pause(): void;
  /** Ramp this source's own gain (0..1) over `ms`. Independent of the bus volume. */
  fade(to: number, ms: number): void;
  /** Current gain, for probes and tests. */
  gain(): number;
  /** Stop, disconnect and release the element. */
  dispose(): void;
}

export interface MediaSourceDeps {
  engine: AudioEngine;
  /** `${assetBase()}` — `/assets` on the web, `/.proxy/assets` inside the Discord activity. */
  base: string;
  /** Format probe; webm/opus wherever it plays, mp3 for Safari. */
  canPlayWebm: boolean;
}

/** `<name>.webm` or `<name>.mp3` under the asset base. */
export function musicUrl(track: MusicTrack, deps: MediaSourceDeps): string {
  return `${deps.base}/music/${track}.${deps.canPlayWebm ? 'webm' : 'mp3'}`;
}

/**
 * Stream one bed into the music bus.
 *
 * Returns null when Web Audio is unavailable or the bus does not exist yet, which is the same
 * contract the rest of `audio/**` keeps: no sound is a degraded session, never a broken one.
 *
 * `loop` rather than a scheduled re-start: the element handles the wrap itself, and with Opus
 * there is no encoder padding to gap it. The mp3 fallback does have padding, so Safari gets a very
 * short seam at the loop point — audible only on a bed with no reverb tail, and cheaper than
 * holding a decoded 78 MB buffer to avoid it.
 */
export function createMediaSource(track: MusicTrack, deps: MediaSourceDeps): MusicSource | null {
  const engine = deps.engine;
  const ctx = engine.ensureContext();
  const bus = engine.bus?.music;
  if (!ctx || !bus || typeof Audio === 'undefined') return null;
  let el: HTMLAudioElement;
  let node: MediaElementAudioSourceNode;
  let gain: GainNode;
  try {
    el = new Audio();
    el.src = musicUrl(track, deps);
    el.loop = true;
    el.preload = 'auto';
    node = ctx.createMediaElementSource(el);
    gain = ctx.createGain();
    gain.gain.value = 0;
    node.connect(gain);
    gain.connect(bus);
  } catch {
    return null;
  }
  let disposed = false;
  return {
    play() {
      if (disposed) return;
      // Rejects with NotAllowedError when the gesture has not landed yet. The director only calls
      // this after the engine reports unlocked, and re-syncs on the next store change, so a
      // rejection here costs a beat rather than the session.
      void el.play().catch(() => undefined);
    },
    pause() {
      if (!disposed) el.pause();
    },
    fade(to, ms) {
      if (disposed) return;
      rampParam(gain.gain, to, ctx.currentTime, ms);
    },
    gain: () => (disposed ? 0 : gain.gain.value),
    dispose() {
      if (disposed) return;
      disposed = true;
      try {
        el.pause();
        // Dropping the src and calling load() is what actually frees the buffered media; leaving
        // the element attached keeps the download alive after a volume-to-zero.
        el.removeAttribute('src');
        el.load();
        node.disconnect();
        gain.disconnect();
      } catch {
        /* already detached */
      }
    },
  };
}

// ---------------------------------------------------------------------------------------------
// Director

export type TimerHandle = ReturnType<typeof setTimeout>;

export interface MusicDirectorOptions {
  /** The process-wide engine (its `bus.music` is the destination). */
  engine: Pick<AudioEngine, 'ensureContext' | 'isUnlocked' | 'onUnlocked' | 'tryUnlock'>;
  /** One store read, shaped for the machine. */
  snapshot: () => MusicSnapshot;
  /** Fires on store changes AND on `visibilitychange`/`focus`; returns an unsubscribe. */
  subscribe: (cb: () => void) => () => void;
  /** Builds a source for a track, or null when one cannot exist. Injected by tests. */
  createSource: (track: MusicTrack) => MusicSource | null;
  crossfadeMs?: number;
  now?: () => number;
  setTimer?: (cb: () => void, ms: number) => TimerHandle;
  clearTimer?: (h: TimerHandle) => void;
}

/**
 * Owns the sources and the crossfade; asks `MusicMachine` what should be playing.
 *
 * Every source is created lazily and kept once created: a match moves between `game` and
 * `final_round` and back to `lobby`, and re-fetching a bed at each boundary would stall the
 * crossfade behind a network round trip. They are released only when the music is switched off.
 */
export class MusicDirector {
  readonly machine = new MusicMachine();
  private readonly sources = new Map<MusicTrack, MusicSource>();
  private readonly crossfadeMs: number;
  private readonly now: () => number;
  private readonly setTimer: (cb: () => void, ms: number) => TimerHandle;
  private readonly clearTimer: (h: TimerHandle) => void;
  private off: (() => void) | null = null;
  private offUnlock: (() => void) | null = null;
  private deadline: TimerHandle | null = null;
  private fadeOut: { track: MusicTrack; handle: TimerHandle } | null = null;
  private track: MusicTrack | null = null;
  private paused = false;
  private disposed = false;

  constructor(private readonly opts: MusicDirectorOptions) {
    this.crossfadeMs = opts.crossfadeMs ?? CROSSFADE_MS;
    this.now = opts.now ?? (() => Date.now());
    this.setTimer = opts.setTimer ?? ((cb, ms) => setTimeout(cb, ms));
    this.clearTimer = opts.clearTimer ?? ((h) => clearTimeout(h));
  }

  /** The bed currently faded up, or null. */
  get current(): MusicTrack | null {
    return this.track;
  }

  /** Track ids with a live source (i.e. something has been fetched for them). */
  get loaded(): MusicTrack[] {
    return [...this.sources.keys()];
  }

  gainOf(track: MusicTrack): number {
    return this.sources.get(track)?.gain() ?? 0;
  }

  /**
   * Subscribe and start. Nothing is fetched until the engine is unlocked, so a player who never
   * touches the page never pays for a bed they could not have heard.
   */
  start(): void {
    if (this.off || this.disposed) return;
    this.off = this.opts.subscribe(() => this.sync());
    this.opts.engine.tryUnlock();
    if (!this.opts.engine.isUnlocked) this.offUnlock = this.opts.engine.onUnlocked(() => this.sync());
    this.sync();
  }

  /** Re-evaluate the plan. Cheap and idempotent; safe to call from any listener. */
  sync(): void {
    if (this.disposed) return;
    const now = this.now();
    const plan = this.machine.plan(this.opts.snapshot(), now);
    this.armDeadline(now);
    if (plan.track === null) {
      this.releaseAll();
      return;
    }
    // Before the unlock a source would be created against a suspended context and `play()` would
    // reject; the machine's answer is remembered by the next `sync` the unlock triggers.
    if (!this.opts.engine.isUnlocked) return;
    if (plan.suspended) {
      this.suspend();
      return;
    }
    this.resume(plan.track);
  }

  private armDeadline(now: number): void {
    if (this.deadline !== null) {
      this.clearTimer(this.deadline);
      this.deadline = null;
    }
    const ms = this.machine.nextDeadlineMs(now);
    if (ms === null) return;
    this.deadline = this.setTimer(() => {
      this.deadline = null;
      this.sync();
    }, ms);
  }

  /** Hidden tab or PIP: silence without losing the position or the buffered media. */
  private suspend(): void {
    if (this.paused) return;
    this.paused = true;
    for (const src of this.sources.values()) src.pause();
  }

  private resume(want: MusicTrack): void {
    if (this.track === want && !this.paused) return;
    const next = this.ensure(want);
    if (!next) return;
    if (this.track === want) {
      // Coming back from a hidden tab: the same bed, already faded up, just stopped.
      this.paused = false;
      next.play();
      next.fade(1, 0);
      return;
    }
    this.paused = false;
    // A fade-out already in flight for the track we are switching back to: cancel its pause so the
    // element is not stopped a beat after being faded up again.
    if (this.fadeOut?.track === want) {
      this.clearTimer(this.fadeOut.handle);
      this.fadeOut = null;
    }
    const prev = this.track === null ? null : this.sources.get(this.track);
    const prevTrack = this.track;
    this.track = want;
    next.play();
    next.fade(1, this.crossfadeMs);
    if (!prev || prevTrack === null) return;
    prev.fade(0, this.crossfadeMs);
    if (this.fadeOut) this.clearTimer(this.fadeOut.handle);
    // Paused rather than disposed: the next switch back should be instant, and a paused element
    // holds its buffer without pulling any more bytes.
    this.fadeOut = {
      track: prevTrack,
      handle: this.setTimer(() => {
        this.fadeOut = null;
        if (this.track !== prevTrack) this.sources.get(prevTrack)?.pause();
      }, this.crossfadeMs),
    };
  }

  private ensure(track: MusicTrack): MusicSource | null {
    const existing = this.sources.get(track);
    if (existing) return existing;
    const created = this.opts.createSource(track);
    if (created) this.sources.set(track, created);
    return created;
  }

  /** Volume at zero, or shutdown: stop the network, drop the elements, forget the bed. */
  private releaseAll(): void {
    if (this.fadeOut) {
      this.clearTimer(this.fadeOut.handle);
      this.fadeOut = null;
    }
    for (const src of this.sources.values()) src.dispose();
    this.sources.clear();
    this.track = null;
    this.paused = false;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.deadline !== null) this.clearTimer(this.deadline);
    this.deadline = null;
    this.off?.();
    this.off = null;
    this.offUnlock?.();
    this.offUnlock = null;
    this.releaseAll();
  }
}
